import * as React from 'react';

import './AlternateProductsModal.scss';
import ImpressionTracker from '../../analytics/impression-tracker/ImpressionTracker';
import {Alerts} from '../../ui/Alerts/Alerts';
import {ContentModal} from '../../ui/modals/ContentModal/ContentModal';
import {GaItemListName} from '../../../client/ga/ga-ecommerce.functions';
import {OrderableItem} from '../item.class';
import {OrderItemsWorkflow} from '../../../client/order-items/order-items.workflow';
import {OrdersService} from '../../../client/orders/orders.service';
import {Price} from '../../pricing/Price';
import {replaceHTMLQuotes} from '../../../client/components/helpers/stringHelper';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import {EmptyState} from '../../ui/EmptyState/EmptyState';
import Button from '../../ui/Buttons/Button';

interface AlternateProductsModalProps {
    alternateProducts: OrderableItem[];
    canEdit: boolean;
    categoryPath?: string;
    errorMessage?: string | JSX.Element;
    onHide: () => void;
    onSelectAlternate?: (itemNum: string) => void;
    ownerType?: `list` | `My Shop`;
    show: boolean;
    showAddToOrder?: boolean;
    user: User;
}

export const AlternateProductsModal = ({
    alternateProducts,
    canEdit,
    categoryPath,
    errorMessage,
    onHide,
    onSelectAlternate,
    ownerType,
    show,
    showAddToOrder,
    user,
}: AlternateProductsModalProps) => {
    const componentName = `AlternateProductsModal`;
    const gaItemListName: GaItemListName = `AlternateProductsModal`;
    const orderItemsWorkflow: OrderItemsWorkflow = useService(`orderItemsWorkflow`);
    const ordersService: OrdersService = useService(`ordersService`);

    /**
     * Template
     */
    return (
        <>
            <ContentModal
                e2eLabel="AlternateProductsModal"
                onClose={onHide}
                show={show}
                title={<div className="tw-text-[1.75rem] tw-font-normal tw-leading-[1.2] tw-mb-0">Alternate Products</div>}
            >
                <>
                    <Alerts
                        message={errorMessage}
                        variant="danger"
                    />
                    {!canEdit && !showAddToOrder && (
                        <Alerts
                            message={`Please contact${ownerType ? ' ' + ownerType : ''} owner to replace discontinued item`}
                            variant="warning"
                        />
                    )}
                    {!alternateProducts?.length && (
                        <EmptyState
                            message={
                                <div className="tw-items-center tw-flex tw-flex-col">
                                    <div className="tw-mb-4">No alternate products are currently available.</div>
                                    {categoryPath && (
                                        <Button
                                            className="tw-w-[150px]"
                                            onClick={() => location.assign(`/browse/${categoryPath}`)}
                                            size="sm"
                                            variant="secondary"
                                        >
                                            Shop Category
                                        </Button>
                                    )}
                                </div>
                            }
                        />
                    )}
                    {alternateProducts?.length > 0 && (
                        <>
                            <div className="tw-overflow-auto">
                                <ul className="tw-flex tw-pl-0">
                                    {alternateProducts.map((alternateProduct, index) => {
                                        return (
                                            <li
                                                className="tw-flex tw-flex-col tw-w-[174px] tw-flex-shrink-0 tw-mr-6"
                                                key={index}
                                            >
                                                <div className="tw-text-center tw-mb-4">
                                                    <ImpressionTracker
                                                        as="span"
                                                        data={{
                                                            dimension16: ordersService?.currentOrderNumber,
                                                            index,
                                                            item_id: alternateProduct.item,
                                                            item_list_name: componentName,
                                                        }}
                                                    >
                                                        <a href={`/item/${alternateProduct.item}`}>
                                                            <img
                                                                alt={replaceHTMLQuotes(alternateProduct.description)}
                                                                src={`/ProductImageThumbs120${
                                                                    alternateProduct.imagePath || `/noimage.png`
                                                                }`}
                                                            />
                                                        </a>
                                                    </ImpressionTracker>
                                                </div>
                                                <div className="tw-text-sm tw-leading-[1.3] tw-font-bold tw-mb-2">
                                                    {user.canViewPrice() && (
                                                        <Price
                                                            alias={componentName}
                                                            item={alternateProduct}
                                                            user={user}
                                                        />
                                                    )}
                                                </div>
                                                <div className="tw-text-sm tw-leading-[1.3] tw-font-normal">
                                                    Item:{' '}
                                                    {alternateProduct.prettyItem ? alternateProduct.prettyItem : alternateProduct.item}
                                                    <br />
                                                    Pkg Qty: {alternateProduct.pkgQty}
                                                </div>
                                                <ImpressionTracker
                                                    as="span"
                                                    data={{
                                                        dimension16: ordersService?.currentOrderNumber,
                                                        index,
                                                        item_id: alternateProduct.item,
                                                        item_list_name: componentName,
                                                    }}
                                                    trackImpressions={false}
                                                >
                                                    <a
                                                        className="tw-text-sm tw-leading-[1.3] tw-font-normal tw-mt-2"
                                                        href={`/item/${alternateProduct.item}`}
                                                        style={{height: 38}}
                                                    >
                                                        {alternateProduct.description}
                                                    </a>
                                                </ImpressionTracker>
                                                <div className="tw-mt-4">
                                                    {showAddToOrder ? (
                                                        <div className="tw-flex tw-flex-row tw-flex-wrap tw-items-center">
                                                            <input
                                                                aria-label="Add to Order Quantity"
                                                                className="tw-h-[calc(1.5em+0.75rem+2px)] tw-py-1.5 tw-px-3 tw-text-base tw-font-normal tw-leading-6 tw-text-gray-600 tw-bg-white tw-border tw-rounded-sm tw-transition-colors tw-duration-150 tw-ease-in-out tw-block sm:tw-inline-block sm:tw-align-middle tw-mr-2 !tw-w-[40px]"
                                                                defaultValue={1}
                                                                id={`cart-qty-${alternateProduct.item}`}
                                                                inputMode="numeric"
                                                                pattern="[0-9]*"
                                                                type="number"
                                                            />
                                                            <Button
                                                                className="tw-w-auto"
                                                                onClick={() => {
                                                                    onHide();
                                                                    // todo I took this input and `cart-qty-${alternateProduct.item}` logic from ItemSpinner.tsx. I plan to convert to tailwind and better react logic in ICOM-12786
                                                                    const unitsOrderedValue = parseInt(
                                                                        (
                                                                            document.getElementById(
                                                                                `cart-qty-${alternateProduct.item}`,
                                                                            ) as HTMLInputElement
                                                                        ).value,
                                                                    );
                                                                    orderItemsWorkflow.addToOrderModal(
                                                                        [
                                                                            {
                                                                                gaEcommerce: {item_list_name: gaItemListName},
                                                                                item: alternateProduct.item,
                                                                                unitsOrdered: unitsOrderedValue,
                                                                            },
                                                                        ],
                                                                        componentName,
                                                                        true,
                                                                    );
                                                                }}
                                                                size="sm"
                                                                variant="primary"
                                                            >
                                                                Add to Order
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <Button
                                                            className="tw-w-full tw-block"
                                                            disabled={canEdit !== true}
                                                            onClick={() => onSelectAlternate(alternateProduct.item)}
                                                            size="sm"
                                                            variant="secondary"
                                                        >
                                                            Select
                                                        </Button>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                            <div className="tw-py-4">
                                Alternate products may not be identical in style or function to original selection.
                            </div>
                        </>
                    )}
                </>
            </ContentModal>
        </>
    );
};
